import React, { useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Grid, 
  Typography, 
  Alert, 
  CircularProgress,
  Card,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import Api from '../../Services/Api';

import { PageHeader, RsocNavHeader } from './';
import MainLayoutDemand from '../MainLoyout/MainLayoutDemand';

// Import new components
import SummaryMetrics from './SummaryMetrics';
import DateFilters from './DateFilters';
import CampaignFilters from './CampaignFilters';
import BudgetDialog from './BudgetDialog';
import BreadcrumbNav from './BreadcrumbNav';
import CampaignTable from './CampaignTable';
import AlertMessage from './AlertMessage';

// Import custom hooks
import useDateFilters from './hooks/useDateFilters';
import usePaginationAndSorting from './hooks/usePaginationAndSorting';
import useAlerts from './hooks/useAlerts';
import useFilters from './hooks/useFilters';
import useViewState from './hooks/useViewState';
import useBlacklist from './hooks/useBlacklist';
import useCampaignData from './hooks/useCampaignData';
import useCampaignActions from './hooks/useCampaignActions';

// Import utilities
import * as campaignUtils from './utils/campaignUtils';
import { 
  getTaboolaCampaignData, 
  getFacebookCampaignData, 
  isTaboolaCampaign, 
  isFacebookCampaign,
  precacheCampaignTypes,
  clearCampaignTypeCache
} from './utils/campaignUtils';

/**
 * RSOC Analytics Component - Displays campaign revenue and cost data
 */
const RsocAnalytics = () => {
  // Initialize custom hooks
  const dateFilters = useDateFilters('today');
  const paginationAndSorting = usePaginationAndSorting('estimated_revenue', 'desc');
  const alertSystem = useAlerts();
  const filters = useFilters();
  const viewState = useViewState();
  
  // Destructure state and methods from hooks for easier access
  const { startDate, endDate, activeFilter, setDateToday, setDateYesterday, setDateRange, 
          handleStartDateChange, handleEndDateChange } = dateFilters;
          
  const { page, rowsPerPage, totalItems, sortField, sortDirection, allDataFetched,
          handlePageChange, handleRowsPerPageChange, handleSortChange } = paginationAndSorting;
          
  const { campaignFilter, platformFilter, handleCampaignFilterChange, 
          handlePlatformFilterChange, facebookAccountId, handleFacebookAccountChange,
          facebookAccounts, isFacebookAccountRequired, isFacebookAccountSelected, validateFilters } = filters;
          
  const { isSubid3View, selectedSubid2, breadcrumbs, enterSubid3View, 
          returnToSubid2View } = viewState;
  
  // State for hourly data view
  const [showHourlyView, setShowHourlyView] = React.useState(false);
  const [hourlyData, setHourlyData] = React.useState(null);
  const [selectedCampaignForHourly, setSelectedCampaignForHourly] = React.useState(null);
  const [hourlyLoading, setHourlyLoading] = React.useState(false);

  // State for channel warnings (missing channels for knowledgia, duplicate channels)
  const [channelWarnings, setChannelWarnings] = React.useState(null);
  
  // Initialize data-dependent hooks
  const campaignData = useCampaignData(
    dateFilters,
    paginationAndSorting,
    filters,
    viewState,
    alertSystem.showError,
    facebookAccountId
  );
  
  const blacklistManager = useBlacklist(alertSystem);
  
  const campaignActions = useCampaignActions(
    alertSystem.showSuccess,
    alertSystem.showError,
    campaignData.fetchReport,
    campaignData.loadCampaignsData,
    campaignData.loadFacebookCampaignsData,
    facebookAccountId,
    campaignData.updateCampaignStatusLocally,
    campaignData.updateCampaignBudgetLocally
  );

  // Destructure state and methods from hooks for easier access
  const { loading, error, combinedData, summaryMetrics, taboolaCampaigns,
          facebookCampaigns, fetchReport, handleClientSideSorting, setLoading,
          resetDataState, clearFacebookData, getValidCombinedData } = campaignData;
          
  const { blacklistedSites, isBlacklisted, handleBlacklistRequest, 
          loadBlacklistForCampaign } = blacklistManager;
          
  const { budgetDialogOpen, selectedCampaign, newBudget, openBudgetDialog,
          handleBudgetDialogClose, handleBudgetChange, handleUpdateBudget,
          handleToggleCampaignStatus } = campaignActions;
          
  const { alertOpen, alertMessage, alertSeverity, handleAlertClose } = alertSystem;

  // Custom handler for sort changes that will use client-side sorting
  const handleTableSortChange = (field) => {
    // Get the new sort state (field and direction)
    const { sortField: newSortField, sortDirection: newSortDirection } = handleSortChange(field);
    
    // Don't fetch new data, instead sort the existing data client-side
    if (combinedData && combinedData.campaigns && combinedData.campaigns.length > 0) {
      // Add special handling for subid3 view
      if (isSubid3View) {
        // Filter out duplicates before sorting by creating a Map based on campaign ID
        const uniqueCampaigns = new Map();
        combinedData.campaigns.forEach(campaign => {
          // Use campaign_id as a key - only keep the last occurrence
          uniqueCampaigns.set(campaign.campaign_id, campaign);
        });
        
        // Convert unique campaigns back to array
        const uniqueCampaignsArray = Array.from(uniqueCampaigns.values());
        
        // Force a more thorough refresh of data representation for subid3 view
        // to maintain reference integrity during the sorting
        const sortedData = handleClientSideSorting(
          { 
            ...combinedData,
            campaigns: uniqueCampaignsArray.map(c => ({...c}))
          },
          newSortField, 
          newSortDirection
        );
        
        // Apply the update with a slight delay to avoid React rendering conflicts
        setTimeout(() => {
          campaignData.setCombinedData(sortedData);
        }, 0);
      } else {
        // Apply standard client-side sorting to the data
        const sortedData = handleClientSideSorting(
          combinedData, 
          newSortField, 
          newSortDirection
        );
        
        // Update the UI with the sorted data
        campaignData.setCombinedData(sortedData);
      }
    }
  };

  // Handle search button click  
  const handleSearch = async (validation = null) => {
    // If validation wasn't passed, validate now
    const validationResult = validation || validateFilters();
    
    if (!validationResult.isValid) {
      alertSystem.showWarning(validationResult.error);
      return;
    }
    
    // Validate dates
    if (!startDate || !endDate) {
      alertSystem.showWarning('Please select both start and end dates');
      return;
    }
    
    console.log(`=== STARTING SEARCH ===`);
    
    try {
      setLoading(true);
      
      // Reset pagination first
      paginationAndSorting.resetPagination();
      
      // AGGRESSIVELY clear ALL data before starting
      console.log('🧹 CLEARING ALL DATA BEFORE SEARCH...');
      resetDataState();
      clearFacebookData();
      
      // Force a longer delay to ensure React state is fully cleared
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Load Facebook campaigns from ALL accounts
      let freshFacebookCampaigns = {};
      if (platformFilter === 'facebook' || platformFilter === 'all') {
        console.log(`📱 LOADING FRESH Facebook campaigns from ALL accounts`);
        const fbResult = await campaignData.loadFacebookCampaignsData(true, null); // null = all accounts
        console.log('📱 Facebook campaigns loaded:', fbResult);
        
        // Verify Facebook campaigns were actually loaded
        if (!fbResult || !fbResult.success) {
          console.error('❌ Facebook campaigns failed to load:', fbResult);
          alertSystem.showWarning('Failed to load Facebook campaigns. Showing partial data.');
        } else {
          freshFacebookCampaigns = fbResult.data || {};
          console.log(`✅ Facebook campaigns loaded successfully: ${Object.keys(freshFacebookCampaigns).length} campaigns from all accounts`);
        }
      }
      
      // Taboola temporarily disabled
      console.log('🔶 SKIPPING Taboola campaigns (temporarily disabled)...');
      // await campaignData.loadCampaignsData(true);
      
      // Fetch fresh report data - we need to ensure Facebook campaigns are available
      console.log('📊 FETCHING FRESH REPORT DATA...');
      console.log('📊 Fresh Facebook campaigns count:', Object.keys(freshFacebookCampaigns).length);
      
      // Pass the fresh Facebook campaigns directly to fetchReport
      // This avoids waiting for React state updates
      const reportResult = await fetchReport(true, freshFacebookCampaigns); // Force refresh with fresh campaigns
      console.log('📊 Report result:', reportResult);
      
      console.log(`=== SEARCH COMPLETED ===`);
      
    } catch (error) {
      console.error('❌ SEARCH ERROR:', error);
      alertSystem.showError('Failed to load campaign data: ' + error.message);
      setLoading(false);
    }
  };
  
  // Use campaignData.handleDownloadCSV directly instead of destructuring it to avoid naming conflicts
  const handleDownloadCSV = () => {
    if (!combinedData || !combinedData.campaigns) {
      return;
    }
    
    campaignData.handleDownloadCSV();
  };

  // Handle refresh button click
  const handleRefresh = async (forceRefresh = false) => {
    if (forceRefresh) {
      // When doing a force refresh, clear and reload campaign data
      setLoading(true);
      try {
        // Clear existing Facebook data first
        console.log('Force refresh: clearing existing Facebook data');
        clearFacebookData();
        
        // Reload Facebook campaigns data from all accounts
        let freshFacebookCampaigns = {};
        if (platformFilter === 'facebook' || platformFilter === 'all') {
          const fbResult = await campaignData.loadFacebookCampaignsData(forceRefresh, null); // null = all accounts
          if (fbResult && fbResult.success) {
            freshFacebookCampaigns = fbResult.data || {};
          }
        }
        
        // Taboola temporarily disabled
        // await campaignData.loadCampaignsData(true);
        
        // Finally fetch the report with the refreshed campaign data
        await fetchReport(true, freshFacebookCampaigns);
      } catch (error) {
        alertSystem.showError('Failed to refresh campaign data');
        setLoading(false);
      }
    } else {
      // For regular refresh, just fetch the report
      await fetchReport();
    }
  };
  
  // Check if only one day is selected
  const isSingleDaySelected = () => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return start.getTime() === end.getTime();
  };
  
  // Function to fetch hourly data for a campaign
  const fetchHourlyDataForCampaign = async (campaignId) => {
    setHourlyLoading(true);
    setSelectedCampaignForHourly(campaignId); // Set this early for loading message

    try {
      const params = {
        start_date: format(new Date(startDate), 'yyyy-MM-dd'),
        end_date: format(new Date(endDate), 'yyyy-MM-dd'),
        subid_2: campaignId
      };

      const response = await Api.getParkingEventsHourly(params);

      if (response.success) {
        setHourlyData(response.data);
        setSelectedCampaignForHourly(campaignId);
        setShowHourlyView(true);
      } else {
        alertSystem.showError('Failed to fetch hourly data: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching hourly data:', error);
      alertSystem.showError('An error occurred while fetching hourly data');
    } finally {
      setHourlyLoading(false);
    }
  };
  
  // Close hourly view
  const closeHourlyView = () => {
    setShowHourlyView(false);
    setHourlyData(null);
    setSelectedCampaignForHourly(null);
    setHourlyLoading(false); // Make sure loading is reset
  };
  
  // Direct and simple drill-down handler
  const handleDrillDown = async (subid2) => {
    if (viewState.viewTransitioning) {
      return;
    }
    
    // Always show hourly data when clicking on a campaign
    await fetchHourlyDataForCampaign(subid2);
    return;
    
    try {
      // Show loading indicator
      setLoading(true);
      
      // Determine platform type based on ID pattern
      const platformType = 
        subid2.startsWith('T-') || /^\d+$/.test(subid2) ? 'taboola' :
        subid2.startsWith('FB-') ? 'facebook' : 
        null;
      
      // Update view state
      enterSubid3View(subid2, platformType);
      
      // Reset pagination and update sorting to cost descending
      paginationAndSorting.resetPagination();
      paginationAndSorting.handleSortChange('cost');
      
      // Set subid3 grouping
      filters.setSubid3GroupBy();
      
      // Load blacklist data
      await loadBlacklistForCampaign(subid2);
      
      // Clear existing data
      resetDataState();
      
      // Create a direct view state object instead of relying on the hook state
      // to update through React's asynchronous state updates
      const directViewState = {
        isSubid3View: true,
        selectedSubid2: subid2,
        platformType: platformType
      };
      
      // Fetch new data with a delay to allow state updates to complete
      setTimeout(() => {
        // Directly invoke fetchReport with a specific overrideViewState parameter
        campaignData.fetchReportWithViewState(true, directViewState);
      }, 300);
    } catch (error) {
      alertSystem.showError('Failed to drill down to detailed view');
      setLoading(false);
    }
  };
  
  // Handle return to main view
  const handleReturnToMainView = () => {
    if (viewState.viewTransitioning) {
      return;
    }
    
    try {
      // Set loading state
      setLoading(true);
      
      // Update view state first
      returnToSubid2View();
      
      // Reset sorting and pagination for main view
      paginationAndSorting.resetPagination();
      paginationAndSorting.handleSortChange('estimated_revenue');
      
      // Set group by for subid2 view
      filters.setSubid2GroupBy();
      
      // Force a complete reset of all data states
      resetDataState();
      blacklistManager.resetBlacklistState();
      
      // Create a direct view state object to explicitly indicate we're going back to main view
      const directMainViewState = {
        isSubid3View: false,
        selectedSubid2: null,
        platformType: null
      };
      
      // Use the improved fetchReportWithViewState function with the explicit main view state
      setTimeout(() => {
        try {
          // Explicitly load Facebook campaigns data first to ensure it's refreshed
          campaignData.loadFacebookCampaignsData(false, facebookAccountId).then(() => {
            // Taboola temporarily disabled
            // return campaignData.loadCampaignsData(true);
            return Promise.resolve();
          }).then(() => {
            // Then fetch the main report with the explicit main view state
            campaignData.fetchReportWithViewState(true, directMainViewState);
          }).catch(error => {
            alertSystem.showError('Failed to refresh data');
            setLoading(false);
          });
        } catch (error) {
          alertSystem.showError('Failed to refresh data');
          setLoading(false);
        }
      }, 300); // Give the view state time to update
    } catch (error) {
      alertSystem.showError('Failed to return to main view');
      setLoading(false);
    }
  };

  // No longer needed - we're loading all accounts together

  // Initialize the component on mount
  useEffect(() => {
    const initializeComponent = async () => {
      // Set initial dates to today
      const today = new Date();
      setDateToday();
      
      // Taboola temporarily disabled
      // Load only Taboola campaigns data initially
      // Facebook campaigns will be loaded when user selects an account and searches
      // await campaignData.loadCampaignsData();
      
      // Don't fetch report automatically - user must select Facebook account and search manually
    };
    
    initializeComponent();
    
    // Clean up cache on component unmount
    return () => {
      clearCampaignTypeCache();
    };
  }, []);

  // Fetch channel warnings on mount
  useEffect(() => {
    const fetchChannelWarnings = async () => {
      try {
        const response = await Api.getRsocChannelWarnings();
        if (response.success) {
          setChannelWarnings(response.data);
        }
      } catch (error) {
        console.error('Error fetching channel warnings:', error);
      }
    };
    fetchChannelWarnings();
  }, []);

  // Return the UI
  return (
    <MainLayoutDemand>
      <Box sx={{ mb: 1.5 }}>
        <PageHeader title="Campaign Revenue & Cost Analytics" />
        <RsocNavHeader />
      </Box>
      
      <Paper 
        sx={{ 
          p: { xs: 1, sm: 1.5 }, 
          mb: 2, 
          borderRadius: 1, 
          boxShadow: 1
        }}
      >
        {/* Compact header with title */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 0.5,
            pb: 0.5,
            borderBottom: '1px solid #f0f0f0' 
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500, 
              fontSize: '0.8rem',
              color: 'text.secondary'
            }}
          >
            Filters
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {/* Date Filters Component */}
          <Grid item xs={12} md={8}>
            <DateFilters 
              startDate={startDate}
              endDate={endDate}
              activeFilter={activeFilter}
              handleStartDateChange={handleStartDateChange}
              handleEndDateChange={handleEndDateChange}
              setDateToday={setDateToday}
              setDateYesterday={setDateYesterday}
              setDateRange={setDateRange}
            />
          </Grid>
          
          {/* Campaign Filters Component */}
          <Grid item xs={12} md={4}>
            <CampaignFilters
              platformFilter={platformFilter}
              handlePlatformFilterChange={handlePlatformFilterChange}
              facebookAccountId={facebookAccountId}
              handleFacebookAccountChange={handleFacebookAccountChange}
              facebookAccounts={facebookAccounts}
              isFacebookAccountRequired={isFacebookAccountRequired}
              validateFilters={validateFilters}
              handleSearch={handleSearch}
              loading={loading}
            />
          </Grid>
        </Grid>
        
        {error && (
          <Alert severity="error" sx={{ mt: 1, py: 0.25 }}>
            {error}
          </Alert>
        )}
      </Paper>
      
      <Box sx={{ mt: 2 }}>
        {loading ? (
          // Show loading state only when actively loading
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={1}>
              {[...Array(6)].map((_, i) => (
                <Grid item xs={12} sm={6} md={2} key={i}>
                  <Card sx={{ p: 1, height: '100%', boxShadow: 1, borderRadius: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" sx={{ mb: 0.5 }}>Loading...</Typography>
                      <CircularProgress size={20} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : !combinedData && !error ? (
          // Initial state - no data yet
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            px: 2,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1
          }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data to Display
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select your date range and filters, then click Search to view campaign analytics.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Facebook data will include all configured accounts automatically.
            </Typography>
          </Box>
        ) : (() => {
          const validData = getValidCombinedData();
          return validData ? (
            // Report data
            <>
              {/* Summary Metrics Component */}
              <SummaryMetrics 
                summaryMetrics={summaryMetrics}
              />
              
              {/* Breadcrumb Navigation */}
              <Box sx={{ mt: 2, mb: 1 }}>
                <BreadcrumbNav 
                  breadcrumbs={breadcrumbs}
                  isSubid3View={isSubid3View}
                  selectedSubid2={selectedSubid2}
                  handleReturnToMainView={handleReturnToMainView}
                />
              </Box>
              
              {/* Hourly Loading State */}
              {hourlyLoading && (
                <Paper sx={{ p: 5, mb: 3, textAlign: 'center' }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Loading Hourly Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fetching performance metrics by hour for {selectedCampaignForHourly}...
                  </Typography>
                </Paper>
              )}

              {/* Hourly Data View */}
              {showHourlyView && !hourlyLoading && (
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Hourly Performance - {selectedCampaignForHourly}
                    </Typography>
                    <Button
                      size="small"
                      onClick={closeHourlyView}
                      variant="outlined"
                    >
                      Back to Campaigns
                    </Button>
                  </Box>
                  
                  {hourlyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
                      <CircularProgress />
                      <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading hourly data...
                      </Typography>
                    </Box>
                  ) : hourlyData && hourlyData.length > 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Performance metrics aggregated by hour across {format(new Date(startDate), 'MMM dd, yyyy')}
                        {startDate !== endDate && ` - ${format(new Date(endDate), 'MMM dd, yyyy')}`}
                        {startDate !== endDate && ' (all days combined by hour)'}
                      </Typography>
                      
                      <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Hour</TableCell>
                              <TableCell align="right">Visits</TableCell>
                              <TableCell align="right">Clicks</TableCell>
                              <TableCell align="right">CTR (%)</TableCell>
                              <TableCell align="right">CVR (%)</TableCell>
                              <TableCell align="right">Revenue ($)</TableCell>
                              <TableCell align="right">FB Spend ($)</TableCell>
                              <TableCell align="right">Profit ($)</TableCell>
                              <TableCell align="right">ROAS</TableCell>
                              <TableCell align="right">EPC ($)</TableCell>
                              <TableCell align="right">CPC ($)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {hourlyData.map((hourData, index) => {
                              // Since backend now aggregates by hour only, we don't need day separation logic
                              // hourData.datetime is now just the hour string like "00:00"
                              
                              return (
                              <TableRow key={index}>
                                <TableCell>
                                  <strong>
                                    {hourData.datetime}
                                  </strong>
                                </TableCell>
                                <TableCell align="right">{hourData.visits.toLocaleString()}</TableCell>
                                <TableCell align="right">{hourData.clicks.toLocaleString()}</TableCell>
                                <TableCell align="right">{hourData.fb_ctr || '0.00'}%</TableCell>
                                <TableCell align="right">{hourData.ctr}%</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color={hourData.estimated_revenue > 0 ? 'success.main' : 'text.secondary'}>
                                    ${hourData.estimated_revenue.toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color={hourData.facebook_spend > 0 ? 'error.main' : 'text.secondary'}>
                                    ${hourData.facebook_spend ? hourData.facebook_spend.toFixed(2) : '0.00'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color={hourData.profit > 0 ? 'success.main' : hourData.profit < 0 ? 'error.main' : 'text.secondary'}>
                                    ${hourData.profit ? hourData.profit.toFixed(2) : '0.00'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color={hourData.roas >= 1 ? 'success.main' : hourData.roas > 0 ? 'warning.main' : 'text.secondary'}>
                                    {hourData.roas ? hourData.roas.toFixed(2) : '0.00'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">${hourData.epc ? hourData.epc.toFixed(4) : '0.0000'}</TableCell>
                                <TableCell align="right">${hourData.cpc ? hourData.cpc.toFixed(4) : '0.0000'}</TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                      
                      {/* Summary */}
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Summary:</strong> {hourlyData.length} hours of data |
                          Total Visits: {hourlyData.reduce((sum, h) => sum + h.visits, 0).toLocaleString()} |
                          Total Clicks: {hourlyData.reduce((sum, h) => sum + h.clicks, 0).toLocaleString()} |
                          Total Revenue: ${hourlyData.reduce((sum, h) => sum + h.estimated_revenue, 0).toFixed(2)} |
                          Total FB Spend: ${hourlyData.reduce((sum, h) => sum + (h.facebook_spend || 0), 0).toFixed(2)} |
                          Total Profit: ${hourlyData.reduce((sum, h) => sum + (h.profit || 0), 0).toFixed(2)} |
                          Overall ROAS: {(() => {
                            const totalRevenue = hourlyData.reduce((sum, h) => sum + h.estimated_revenue, 0);
                            const totalSpend = hourlyData.reduce((sum, h) => sum + (h.facebook_spend || 0), 0);
                            return totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : '0.00';
                          })()}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No hourly data available for this campaign.
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Campaign Table Component */}
              {!hourlyLoading && (
              <CampaignTable
                key={`table-${facebookAccountId || 'none'}`}
                combinedData={validData}
                loading={loading}
                isSubid3View={isSubid3View}
                selectedSubid2={selectedSubid2}
                showHourlyHint={true}
                platformFilter={platformFilter}
                sortField={sortField}
                sortDirection={sortDirection}
                rowsPerPage={rowsPerPage}
                page={page}
                totalItems={totalItems}
                handleSortChange={handleTableSortChange}
                handleRefresh={handleRefresh}
                handleDownloadCSV={handleDownloadCSV}
                handleDrillDown={handleDrillDown}
                getTaboolaCampaignData={(campaignId) => getTaboolaCampaignData(campaignId, validData, taboolaCampaigns, isSubid3View)}
                isTaboolaCampaign={(campaignId) => isTaboolaCampaign(campaignId, validData, taboolaCampaigns)}
                getFacebookCampaignData={(campaignId) => {
                  // Use the facebookCampaigns from campaignData hook to ensure we're using the right data
                  const currentFacebookCampaigns = campaignData.facebookCampaigns || {};
                  if (currentFacebookCampaigns && campaignId) {
                    console.log(`🎯 RsocAnalytics getting FB data for: ${campaignId}`);
                    console.log(`🎯 Available facebookCampaigns:`, Object.keys(currentFacebookCampaigns).slice(0, 3));
                    const result = campaignUtils.getFacebookCampaignData(campaignId, validData, currentFacebookCampaigns);
                    console.log(`🎯 getFacebookCampaignData result:`, result ? 'FOUND' : 'NOT FOUND');
                    return result;
                  }
                  return null;
                }}
                isFacebookCampaign={(campaignId) => isFacebookCampaign(campaignId, validData, campaignData.facebookCampaigns || {})}
                openBudgetDialog={openBudgetDialog}
                toggleCampaignStatus={handleToggleCampaignStatus}
                handleBlacklist={handleBlacklistRequest}
                handlePageChange={handlePageChange}
                handleRowsPerPageChange={handleRowsPerPageChange}
                isBlacklisted={isBlacklisted}
                allDataFetched={allDataFetched}
                facebookCampaigns={campaignData.facebookCampaigns || {}}
                channelWarnings={channelWarnings}
              />
              )}
            </>
          ) : (
            // Show empty state or loading when data doesn't match selected account
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={1}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={2} key={i}>
                    <Card sx={{ p: 1, height: '100%', boxShadow: 1, borderRadius: 1 }}>
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" sx={{ mb: 0.5 }}>Loading account data...</Typography>
                        <CircularProgress size={20} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })()}
      </Box>
      
      {/* Budget Dialog Component */}
      <BudgetDialog
        open={budgetDialogOpen}
        selectedCampaign={selectedCampaign}
        newBudget={newBudget}
        handleClose={handleBudgetDialogClose}
        handleBudgetChange={handleBudgetChange}
        handleUpdateBudget={handleUpdateBudget}
      />
      
      {/* Alert Message Component */}
      <AlertMessage
        open={alertOpen}
        message={alertMessage}
        severity={alertSeverity}
        handleClose={handleAlertClose}
      />
    </MainLayoutDemand>
  );
};

export default RsocAnalytics;